import { LoadingArea } from '@consigli/facade';
import { useGetProjectQuery } from '@consigli/hooks';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router';

import { AgentsLoading } from '@/components/agents-loading';
import { FeatureFlag, useFeatureFlag, useFeatureFlagsReady } from '@/feature-flags';
import { AppShell } from '@/layouts/app-shell/app-shell';
import { PageNotFound } from '@/layouts/page-not-found';
import { Route } from '@/routes';

import { AgentsTab } from './agents-tab';
import { ProjectOverviewError } from './project-overview-error';
import { ProjectOverviewHeader } from './project-overview-header';
import { SettingsTab } from './settings-tab';
import { UsersTab } from './users-tab';

/** The three tabs the project workspace routes to. */
export type ProjectOverviewTab = Route.AGENTS | Route.USERS | Route.SETTINGS;

interface ProjectOverviewRouteProps {
  /** Which of the three URLs rendered this. */
  tab: ProjectOverviewTab;
}

/**
 * The project workspace: one route component behind all three tab URLs.
 *
 * Settings has no tab body yet, so it redirects to Agents. It is still a route
 * rather than being left to the 404 splat — the redirect is what stops a pilot
 * user's shared link breaking for a colleague, and the route table is built at
 * module scope and cannot read a flag, so all three have to exist in both flag
 * states.
 *
 * Flag-lifetime code only in its gate: when the flag dies the gate and the
 * `/services` redirect go with it, and this becomes the page.
 */
export const ProjectOverviewRoute: FC<ProjectOverviewRouteProps> = ({ tab }) => {
  const { t } = useTranslation();
  const { projectId: projectIdParam } = useParams();
  const ready = useFeatureFlagsReady();
  const redesigned = useFeatureFlag(FeatureFlag.AppRedesign);

  // Exact, not Number.isFinite on a parse: Number.parseInt('42abc') is 42, so a
  // parse-and-check would silently load project 42 from /projects/42abc/agents
  // and the tab bar would then rewrite its hrefs to the clean id.
  const isValidId = /^\d+$/.test(projectIdParam ?? '');
  const projectId = Number(projectIdParam);

  // `useGetProjectQuery` directly rather than `useProject`, which wraps it but
  // exposes no `skip` — and a guard placed after an unconditional hook cannot
  // stop the query, so the id check below would be decorative and `NaN` would
  // reach the API. Not worth adding `skip` to the lib hook for one caller: that
  // turns this into a cross-package change with a @consigli/hooks rebuild.
  const {
    data: project,
    error,
    fulfilledTimeStamp,
    isError,
    isFetching,
    isLoading,
    refetch,
  } = useGetProjectQuery(
    { projectId },
    // Every tab needs the response now: the header's title and the breadcrumb
    // come from the project rather than the tab, and Settings edits it.
    // Refetched on every mount, because the project list seeds this entry so
    // the page draws at once: the seed is shown, never trusted.
    { refetchOnMountOrArgChange: true, skip: !isValidId },
  );

  // Settings edits the project, so it waits for a response fetched since this
  // route mounted rather than drawing a form from the list's seed, which the
  // refetch could then reset under the user's typing.
  const [mountedAt] = useState(() => Date.now());
  const isFetchedSinceMount = (fulfilledTimeStamp ?? 0) >= mountedAt;
  const settingsWaits = tab === Route.SETTINGS && !isFetchedSinceMount && !isError;

  // Absolute, and built from the raw param: these are single routes with
  // two-segment paths (`:projectId/agents`), so react-router's default
  // `relative="route"` resolves `..` against the parent *route* — `projects` —
  // and `<Navigate to="../services">` would land on /projects/services. The
  // target is pinned in the route test, because this is a mistake that looks
  // right.
  const serviceListHref = `/${Route.PROJECTS}/${projectIdParam}/${Route.SERVICES}`;

  // `AppShell` requires breadcrumbs, so there is no "render the shell without a
  // project" path — the crumb falls back to Home alone until the name arrives.
  const homeCrumb = { href: `/${Route.PROJECTS}`, label: t('navbar.menu.home') };

  if (!ready) {
    return (
      <div className="flex justify-center items-center">
        <LoadingArea title={t('service-list.loading-title')} className="mx-auto mt-[25vh]" />
      </div>
    );
  }

  // The flag gate comes first so the off state never renders the redesign's
  // shell. These URLs do not exist in the old app, so a malformed id is a 404
  // there — under the legacy PageHeader, which is not suppressed while the flag
  // is off — exactly as the router's splat answers it today.
  if (!redesigned) {
    return isValidId ? <Navigate replace to={serviceListHref} /> : <PageNotFound />;
  }

  // Inside the shell, because the flag suppresses the legacy PageHeader on this
  // URL whether or not the id parses. A bare PageNotFound here would paint with
  // no side nav, no top bar and no header at all.
  if (!isValidId) {
    return (
      <AppShell breadcrumbs={[homeCrumb]}>
        {/* Not the default h-screen: the shell has already spent height on the
            top bar, so a viewport-height block here adds dead scroll. */}
        <PageNotFound className="min-h-[60vh]" />
      </AppShell>
    );
  }

  if (isLoading || settingsWaits) {
    // The chrome stays up and the wait sits under it, as the frames draw it —
    // the tabs and the invite action work while the name is still loading, and
    // nothing reflows when it lands.
    //
    // AgentsLoading rather than the facade LoadingArea: this is the same wait
    // the legacy service list shows, and it has to look the same across the flag
    // flip. Routing the /services index to /agents otherwise strands the marquee
    // on the one page flag-off users can reach.
    //
    // The Users & Invites tab does not wait here at all. It reads the id from
    // the URL rather than the project, so its two lists can be in flight
    // alongside the project fetch instead of behind it, and it draws its own
    // placeholder rows.
    //
    // Settings does wait, and shows nothing but the chrome while it does. Every
    // one of its fields is a value from the project, so there is no partial
    // rendering to offer — and the agent marquee below would be a plainly wrong
    // thing to show above a settings form.
    return (
      <AppShell
        banner={<ProjectOverviewHeader projectId={String(projectId)} />}
        breadcrumbs={[homeCrumb]}
      >
        {tab === Route.USERS && <UsersTab />}
        {tab === Route.AGENTS && <AgentsLoading />}
      </AppShell>
    );
  }

  const status = (error as { status?: number } | undefined)?.status;
  const denied = status === 403 || status === 404;

  // A failed refetch keeps the page it has: RTK holds the last data on error,
  // and the mount refetch fails on a blip that the page does not need to hear
  // about. Three cases still show the error: no project to draw, the server now
  // refusing it, and Settings, which would otherwise edit an unconfirmed seed.
  const refetchFailedOnSeed = isError && tab === Route.SETTINGS && !isFetchedSinceMount;
  if (project === undefined || (isError && denied) || refetchFailedOnSeed) {
    // 403 and 404 are one arm — see ProjectOverviewError. Anything else,
    // including a network failure with a non-numeric status, is the retryable
    // arm. `project === undefined` without `isError` is not reachable through
    // the hook's own states; it is here so the success branch below can read the
    // project without a non-null assertion.
    return (
      <AppShell breadcrumbs={[homeCrumb]}>
        <ProjectOverviewError
          isRetrying={isFetching}
          onRetry={() => void refetch()}
          variant={denied ? 'no-access' : 'unavailable'}
        />
      </AppShell>
    );
  }

  return (
    <AppShell
      banner={<ProjectOverviewHeader project={project} projectId={String(projectId)} />}
      breadcrumbs={[homeCrumb, { label: project.name }]}
    >
      {tab === Route.USERS && <UsersTab />}
      {tab === Route.SETTINGS && <SettingsTab project={project} />}
      {tab === Route.AGENTS && <AgentsTab />}
    </AppShell>
  );
};
